import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trash2, Phone, Mail, MessageCircle, Store, Clock, StickyNote, Megaphone } from 'lucide-react';

const API = window.location.origin + '/api';

const STATUS_META = {
  new:        { label: 'New',        bg: '#DBEAFE', color: '#1E3A8A' },
  contacted:  { label: 'Contacted',  bg: '#FEF3C7', color: '#92400E' },
  qualified:  { label: 'Qualified',  bg: '#D1FAE5', color: '#065F46' },
  closed:     { label: 'Closed Won', bg: '#DCFCE7', color: '#166534' },
  rejected:   { label: 'Rejected',   bg: '#FEE2E2', color: '#991B1B' },
};

const STATUSES = ['new', 'contacted', 'qualified', 'closed', 'rejected'];

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function waLink(phone, name) {
  const cleaned = (phone || '').replace(/[^0-9+]/g, '').replace(/^\+/, '');
  if (!cleaned) return null;
  const text = encodeURIComponent(
    `Hi ${name || 'there'} 👋 This is StoreRuns following up on your Merchant Kit inquiry. Ready to set up your free 30-day trial?`
  );
  return `https://wa.me/${cleaned}?text=${text}`;
}

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [notesDraft, setNotesDraft] = useState({});
  const navigate = useNavigate();

  const load = () => {
    axios.get(`${API}/merchant-kit/admin/leads`, { withCredentials: true })
      .then(r => setLeads(r.data.leads || []))
      .catch(err => console.error('Failed to load leads:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateLead = async (id, patch) => {
    try {
      await axios.put(`${API}/merchant-kit/admin/leads/${id}`, patch, { withCredentials: true });
      setLeads(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l)));
    } catch (err) {
      console.error('Failed to update lead:', err);
      alert('Could not update lead');
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await axios.delete(`${API}/merchant-kit/admin/leads/${id}`, { withCredentials: true });
      setLeads(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error('Failed to delete lead:', err);
      alert('Could not delete lead');
    }
  };

  const saveNotes = (id) => {
    const draft = notesDraft[id];
    if (draft === undefined) return;
    updateLead(id, { notes: draft });
    setNotesDraft(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const visible = filter === 'all' ? leads : leads.filter(l => (l.status || 'new') === filter);
  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = leads.filter(l => (l.status || 'new') === s).length;
    return acc;
  }, {});

  return (
    <div className="admin-page" data-testid="admin-leads-page" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Admin
      </button>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 4px' }}>
        Merchant Leads Inbox
        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 500 }}>
          ({leads.length} total)
        </span>
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 20px' }}>
        Supermarket owners who submitted the Merchant Kit form. Follow up within one business day.
      </p>

      {/* Status filters */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }} data-testid="lead-filters">
        <button
          onClick={() => setFilter('all')}
          data-testid="filter-all"
          style={{
            padding: '7px 14px', borderRadius: 999,
            background: filter === 'all' ? 'var(--navy)' : '#f1f5f9',
            color: filter === 'all' ? 'white' : '#475569',
            border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
          }}
        >
          All ({leads.length})
        </button>
        {STATUSES.map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            style={{
              padding: '7px 14px', borderRadius: 999,
              background: filter === s ? STATUS_META[s].color : STATUS_META[s].bg,
              color: filter === s ? 'white' : STATUS_META[s].color,
              border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
            }}
          >
            {STATUS_META[s].label} ({counts[s]})
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading leads…</div>}

      {!loading && visible.length === 0 && (
        <div data-testid="leads-empty" style={{
          textAlign: 'center', padding: '60px 20px', background: 'var(--white)',
          borderRadius: 14, border: '1px dashed var(--border-gray)', color: 'var(--text-muted)'
        }}>
          <p style={{ margin: 0, fontSize: '0.95rem' }}>
            {filter === 'all'
              ? 'No leads yet. Share your Merchant Kit link to start collecting inquiries.'
              : `No leads with status "${STATUS_META[filter]?.label || filter}".`}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {visible.map(lead => {
          const status = lead.status || 'new';
          const meta = STATUS_META[status] || STATUS_META.new;
          const wa = waLink(lead.phone, lead.name);
          const draftNotes = notesDraft[lead.id] ?? lead.notes ?? '';
          const notesDirty = notesDraft[lead.id] !== undefined && notesDraft[lead.id] !== (lead.notes || '');

          return (
            <div
              key={lead.id}
              data-testid={`lead-card-${lead.id}`}
              style={{
                background: 'var(--white)', border: '1px solid var(--border-gray)',
                borderLeft: `4px solid ${meta.color}`, borderRadius: 12, padding: 18
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--navy)' }}>{lead.name}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <Store size={13} /> {lead.supermarket_name}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    data-testid={`lead-status-${lead.id}`}
                    style={{ background: meta.bg, color: meta.color, padding: '4px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.05 }}
                  >
                    {meta.label}
                  </span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12, fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155' }}>
                  <Phone size={14} /> <a href={`tel:${lead.phone}`} style={{ color: '#334155' }}>{lead.phone}</a>
                </div>
                {lead.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155' }}>
                    <Mail size={14} /> <a href={`mailto:${lead.email}`} style={{ color: '#334155' }}>{lead.email}</a>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}>
                  <Clock size={14} /> {formatDate(lead.created_at)}
                </div>
              </div>

              {lead.message && (
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: '0.88rem', color: '#334155', fontStyle: 'italic' }}>
                  &ldquo;{lead.message}&rdquo;
                </div>
              )}

              {(lead.utm_source || lead.utm_campaign || lead.utm_medium) && (
                <div data-testid={`lead-utm-${lead.id}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {lead.utm_source && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EEF2FF', color: '#4338CA', padding: '3px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600 }}>
                      <Megaphone size={11} /> {lead.utm_source}
                    </span>
                  )}
                  {lead.utm_medium && (
                    <span style={{ background: '#F3F4F6', color: '#374151', padding: '3px 8px', borderRadius: 999, fontSize: '0.72rem' }}>{lead.utm_medium}</span>
                  )}
                  {lead.utm_campaign && (
                    <span style={{ background: '#FEF3C7', color: '#92400E', padding: '3px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600 }}>{lead.utm_campaign}</span>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.05, display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                  <StickyNote size={12} /> Internal notes
                </label>
                <textarea
                  value={draftNotes}
                  onChange={(e) => setNotesDraft(prev => ({ ...prev, [lead.id]: e.target.value }))}
                  data-testid={`lead-notes-${lead.id}`}
                  placeholder="Follow-up date, who you spoke to, next step..."
                  rows={2}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border-gray)', fontFamily: 'inherit', fontSize: '0.85rem', resize: 'vertical' }}
                />
                {notesDirty && (
                  <button
                    onClick={() => saveNotes(lead.id)}
                    data-testid={`lead-notes-save-${lead.id}`}
                    style={{ marginTop: 6, padding: '5px 12px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Save notes
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={status}
                  onChange={(e) => updateLead(lead.id, { status: e.target.value })}
                  data-testid={`lead-status-select-${lead.id}`}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-gray)', fontSize: '0.85rem', fontFamily: 'inherit', background: 'white', cursor: 'pointer' }}
                >
                  {STATUSES.map(s => (
                    <option key={s} value={s}>{STATUS_META[s].label}</option>
                  ))}
                </select>
                {wa && (
                  <a
                    href={wa}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`lead-whatsapp-${lead.id}`}
                    style={{ padding: '7px 12px', background: '#25D366', color: 'white', borderRadius: 8, textDecoration: 'none', fontSize: '0.82rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                )}
                <button
                  onClick={() => deleteLead(lead.id)}
                  data-testid={`lead-delete-${lead.id}`}
                  style={{ marginLeft: 'auto', padding: '7px 12px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
